import type { EduCourse, EduSchool } from '@common/types/edu-course.type';
import type { EduCoursesCollectionState } from './edu-courses.reducer';
import type { CollectionsState } from '../collections-feature.reducer';

import { createSelector } from '@ngrx/store';

import { EDU_COURSES_COLLECTIONS_FEATURE_KEY, eduCoursesAdapter } from './edu-courses.reducer';
import { collectionsState } from '../collections-state.selectors';
import { selectEduSchools } from '../edu-schools/edu-schools.selectors';
import { selectEduCoursesTariffsState } from '../edu-courses-tariffs/edu-courses-tariffs.selectors';
import { CollectionStateStatus } from '@common/adapters/collections';

const { selectCollection, selectCollectionStatuses } = eduCoursesAdapter.getSelectors();

const selectEduCoursesState = createSelector(
    collectionsState,
    (state: CollectionsState): EduCoursesCollectionState => state[EDU_COURSES_COLLECTIONS_FEATURE_KEY]
);

export const selectEduCourses = createSelector(selectEduCoursesState, selectCollection);

export const selectIsEduCoursesEmpty = createSelector(
    selectEduCourses,
    (eduCourses: EduCourse[]): boolean => !eduCourses || eduCourses?.length === 0
);

export const selectEduCoursesStatuses = createSelector(selectEduCoursesState, selectCollectionStatuses);

export const selectEduCoursesBySiteId = (siteId: string) =>
    createSelector(selectEduCourses, selectEduSchools, (eduCourses: EduCourse[], eduSchools: EduSchool[]) => {
        const eduSchool = eduSchools.find((school: EduSchool) => school?.landingId?.toString() === siteId);
        return eduCourses.filter(
            (course: EduCourse) => course?.landingId?.toString() === siteId || course.schoolId === eduSchool?.id
        );
    });

export const selectEduCoursesWithTariffs = createSelector(
    selectEduCourses,
    selectEduCoursesTariffsState,
    (courses: EduCourse[], tariffsState) => {
        // Until the tariffs collection has loaded there is nothing to join against.
        // Returning an empty list (rather than a list of `undefined` entries, one per
        // course) keeps every consumer — `sortByPaid`, the site-id filter, the
        // `filter(courses.length)` backfills — from dereferencing a missing course.
        if (tariffsState.status !== CollectionStateStatus.success) {
            return [];
        }

        return (courses ?? []).map((item) => ({
            ...item,
            tariffs: tariffsState.items.filter((tariff) => tariff.courseId === item.id),
        }));
    }
);

export const selectEduCoursesWithTariffsBySiteId = (siteId: string) =>
    createSelector(selectEduCoursesWithTariffs, selectEduSchools, (courses, eduSchools) => {
        if (!courses || !eduSchools) {
            return [];
        }

        const eduSchool = eduSchools.find((school: EduSchool) => school?.landingId?.toString() === siteId);
        return courses?.filter(
            (course: EduCourse) => course?.landingId?.toString() === siteId || course.schoolId === eduSchool?.id
        );
    });
